export type ConsultationRepeaterScalarType =
  | 'text'
  | 'longText'
  | 'number'
  | 'decimal'
  | 'boolean'
  | 'date'
  | 'time'
  | 'dateTime'
  | 'singleSelect'
  | 'multiSelect';

export type ConsultationRepeaterFieldSchema = {
  key: string;
  label?: string;
  type: ConsultationRepeaterScalarType | 'repeater';
  required?: boolean;
  minItems?: number;
  maxItems?: number;
  itemLabel?: string;
  options?: Array<{ id: string; label: string }>;
  fields?: ConsultationRepeaterFieldSchema[];
};

export type ConsultationRepeaterSchema = {
  rootKey?: string;
  minItems?: number;
  maxItems?: number;
  itemLabel?: string;
  fields: ConsultationRepeaterFieldSchema[];
};

export const CATEGORIES_WITH_ITEMS_PRESET_SCHEMA: ConsultationRepeaterSchema = {
  rootKey: 'categories',
  minItems: 0,
  maxItems: 50,
  itemLabel: 'Category',
  fields: [
    {
      key: 'category',
      label: 'Category name',
      type: 'text',
      required: true,
    },
    {
      key: 'items',
      type: 'repeater',
      itemLabel: 'Item',
      minItems: 0,
      maxItems: 100,
      fields: [
        { key: 'name', label: 'Name', type: 'text', required: true },
        { key: 'date', label: 'Date', type: 'date', required: false },
      ],
    },
  ],
};

export const parseConsultationRepeaterSchema = (schemaJson?: string): ConsultationRepeaterSchema | null => {
  if (!schemaJson?.trim()) {
    return null;
  }

  try {
    const parsed = JSON.parse(schemaJson) as ConsultationRepeaterSchema;
    if (!parsed || !Array.isArray(parsed.fields) || parsed.fields.length === 0) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

export const createEmptyRepeaterValue = (schema: ConsultationRepeaterSchema): Record<string, unknown> => {
  if (schema.rootKey?.trim()) {
    return { [schema.rootKey.trim()]: [] };
  }

  return { items: [] };
};

export const parseRepeaterValue = (
  schema: ConsultationRepeaterSchema,
  rawValue: string | undefined,
): Record<string, unknown> => {
  if (!rawValue?.trim()) {
    return createEmptyRepeaterValue(schema);
  }

  try {
    const parsed = JSON.parse(rawValue) as Record<string, unknown>;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return createEmptyRepeaterValue(schema);
    }

    return parsed;
  } catch {
    return createEmptyRepeaterValue(schema);
  }
};

export const serializeRepeaterValue = (value: Record<string, unknown>): string => JSON.stringify(value);

const getItemsArray = (root: Record<string, unknown>, schema: ConsultationRepeaterSchema): unknown[] => {
  if (schema.rootKey?.trim()) {
    const items = root[schema.rootKey.trim()];
    return Array.isArray(items) ? items : [];
  }

  const direct = root.items;
  return Array.isArray(direct) ? direct : [];
};

const setItemsArray = (
  root: Record<string, unknown>,
  schema: ConsultationRepeaterSchema,
  items: unknown[],
): Record<string, unknown> => {
  if (schema.rootKey?.trim()) {
    return { ...root, [schema.rootKey.trim()]: items };
  }

  return { ...root, items };
};

const isEmptyScalar = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return true;
  }

  if (typeof value === 'string') {
    return value.trim() === '';
  }

  return false;
};

const validateScalarValue = (
  field: ConsultationRepeaterFieldSchema,
  rawValue: unknown,
  path: string,
): string | null => {
  if (isEmptyScalar(rawValue)) {
    return field.required ? `${path} is required` : null;
  }

  const value = typeof rawValue === 'boolean'
    ? (rawValue ? 'true' : 'false')
    : String(rawValue);

  switch (field.type) {
    case 'number':
      return /^-?\d+$/.test(value) ? null : `${path} must be a valid integer`;
    case 'decimal':
      return Number.isFinite(Number(value)) ? null : `${path} must be a valid decimal number`;
    case 'boolean':
      return value === 'true' || value === 'false' ? null : `${path} must be true or false`;
    case 'date':
      return /^\d{4}-\d{2}-\d{2}$/.test(value) ? null : `${path} must be in yyyy-MM-dd format`;
    case 'time':
      return /^(\d{2}):(\d{2})(?::(\d{2}))?$/.test(value) ? null : `${path} must be in HH:mm or HH:mm:ss format`;
    case 'dateTime':
      return Number.isFinite(Date.parse(value)) ? null : `${path} must be a valid date-time`;
    case 'singleSelect':
      if (!field.options?.some((option) => option.id === value)) {
        return `${path} must match one of the configured option ids`;
      }
      return null;
    case 'multiSelect': {
      const ids = new Set(field.options?.map((option) => option.id) ?? []);
      const values = value.split(',').map((item) => item.trim()).filter(Boolean);
      if (values.length === 0) {
        return `${path} must include at least one option id`;
      }
      if (new Set(values).size !== values.length) {
        return `${path} contains duplicate option ids`;
      }
      if (values.some((item) => !ids.has(item))) {
        return `${path} must only contain configured option ids`;
      }
      return null;
    }
    default:
      return null;
  }
};

const validateObjectAgainstFields = (
  item: Record<string, unknown>,
  fields: ConsultationRepeaterFieldSchema[],
  path: string,
  strictKeys: boolean,
): string | null => {
  if (strictKeys) {
    const allowedKeys = new Set(fields.map((field) => field.key));
    for (const key of Object.keys(item)) {
      if (!allowedKeys.has(key)) {
        return `${path} contains unknown property '${key}'`;
      }
    }
  }

  for (const field of fields) {
    const fieldPath = `${path}.${field.key}`;
    const propertyValue = item[field.key];

    if (field.type === 'repeater') {
      if (propertyValue === undefined) {
        if (field.required) {
          return `${fieldPath} is required`;
        }
        continue;
      }

      if (!Array.isArray(propertyValue)) {
        return `${fieldPath} must be a JSON array`;
      }

      const nestedSchema: ConsultationRepeaterSchema = {
        fields: field.fields ?? [],
        minItems: field.minItems,
        maxItems: field.maxItems,
      };
      const nestedError = validateRepeaterItems(propertyValue, nestedSchema, fieldPath);
      if (nestedError) {
        return nestedError;
      }

      continue;
    }

    const scalarError = validateScalarValue(field, propertyValue, fieldPath);
    if (scalarError) {
      return scalarError;
    }
  }

  return null;
};

const validateRepeaterItems = (
  items: unknown[],
  schema: ConsultationRepeaterSchema,
  path: string,
): string | null => {
  const minItems = schema.minItems ?? 0;
  const maxItems = schema.maxItems ?? Number.MAX_SAFE_INTEGER;

  if (items.length < minItems) {
    return `${path} must include at least ${minItems} item(s)`;
  }

  if (items.length > maxItems) {
    return `${path} must include at most ${maxItems} item(s)`;
  }

  for (let index = 0; index < items.length; index += 1) {
    const item = items[index];
    const itemPath = `${path}[${index}]`;
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      return `${itemPath} must be a JSON object`;
    }

    const itemError = validateObjectAgainstFields(
      item as Record<string, unknown>,
      schema.fields,
      itemPath,
      true,
    );
    if (itemError) {
      return itemError;
    }
  }

  return null;
};

export const validateConsultationRepeaterValue = (
  schemaJson: string | undefined,
  rawValue: string | undefined,
): string | null => {
  const schema = parseConsultationRepeaterSchema(schemaJson);
  if (!schema) {
    return 'field definition schemaJson is invalid';
  }

  if (!rawValue?.trim()) {
    return null;
  }

  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(rawValue) as Record<string, unknown>;
  } catch {
    return 'value must be valid JSON';
  }

  const items = getItemsArray(parsed, schema);
  return validateRepeaterItems(items, schema, 'value');
};

export const hasConsultationRepeaterValue = (
  schemaJson: string | undefined,
  rawValue: string | undefined,
): boolean => {
  const schema = parseConsultationRepeaterSchema(schemaJson);
  if (!schema || !rawValue?.trim()) {
    return false;
  }

  try {
    const parsed = JSON.parse(rawValue) as Record<string, unknown>;
    const items = getItemsArray(parsed, schema);
    if (items.length > 0) {
      return true;
    }

    return (schema.minItems ?? 0) > 0;
  } catch {
    return false;
  }
};

export const repeaterSchemaHelpers = {
  getItemsArray,
  setItemsArray,
};
